import { getCustomProductTiles } from '@features/products/api/get-custom-product-grid-tiles'
import { getProductTiles } from '@features/products/api/get-product-tiles'
import { mapEdgesToItems } from '@utils/map'
import { MediaPicker } from 'types/mediaPicker'
import { ReactComponentClearDefinition } from 'types/page'

import { ParsedUrlQuery } from 'querystring'
import { isAvailableForPurchase } from './is-available-for-purchase'
import { getRetailers } from './umbraco'
import { getCountryConfig, getCurrencyFromCountry } from './utils'

export async function getComponentProps(
  page: any,
  queryParams: ParsedUrlQuery,
  alpha2code: string,
) {
  if (!page.components) return

  page.components = await Promise.all(
    page.components!.map(
      async (component, index): Promise<ReactComponentClearDefinition> => {
        const channel = getCountryConfig(alpha2code).channel
        const currency = getCurrencyFromCountry(alpha2code)

        try {
          switch (component.__typename) {
            case 'BrandStory':
              const UBComputedChapters = []
              let startIndex = 0
              for (let i = 0; i < component.chapters.length; i++) {
                const curChapter = component.chapters[i]
                const prevChapter = component.chapters[i - 1]

                startIndex += i === 0 ? 0 : prevChapter.pages.length

                UBComputedChapters.push({
                  startIndex,
                  ...curChapter,
                })
              }
              component.content = UBComputedChapters
              break

            // RETAILERS
            case 'StoreLocatorComponent':
              const retailers = await getRetailers()
              component.retailers = retailers
              break
            // Login Form
            case 'LoginForm':
              component.pageId = page.restrictedId ?? null

            // PRODUCT GRID
            case 'ProductSlider':
              if (component.variantsV2) {
                const variantIds = component.variantsV2.map(String)
                let query = null

                if (variantIds && variantIds.length > 0) {
                  query = variantIds
                    .map((variantId) => `variant_id:"${variantId}"`)
                    .join(' OR ')
                }

                const products = await getProductTiles({
                  channel,
                  currency,
                  query,
                  includeAllVariants: true,
                })

                if (!products) {
                  component.products = []
                  break
                }

                // THE TYPES HERE IS IGNORERD, SHOULD PROPERLY BE HANDLE, BUT IT'S A QUICK FIX
                const splittedProducts = products
                  .map((product: any) => {
                    const innerVariants = mapEdgesToItems(product.variants)
                    const innerProducts = innerVariants
                      .filter((v: any) => variantIds.includes(v.id))
                      .map((variant) => {
                        const obj = {
                          ...product,
                          heroVariant: variant,
                        }
                        delete obj.variants
                        return obj
                      })
                    return innerProducts
                  })
                  .flat()
                  .sort((a, b) => {
                    return (
                      variantIds.indexOf(a.heroVariant.id) -
                      variantIds.indexOf(b.heroVariant.id)
                    )
                  })

                component.products = splittedProducts
                break
              } else if (component.productsV2) {
                const productIds = component.productsV2.map(String)
                let query = null

                if (productIds && productIds.length > 0) {
                  query = productIds
                    .map((productId) => `id:"${productId}"`)
                    .join(' OR ')
                }

                const products = await getProductTiles({
                  channel,
                  currency,
                  query,
                })

                //sort the products based on the order of the productV2
                if (productIds) {
                  products.sort((a, b) => {
                    const indexA = productIds.findIndex((x) => x == a.id)
                    const indexB = productIds.findIndex((x) => x == b.id)
                    if (indexA === -1) return 1
                    if (indexB === -1) return -1
                    return indexA - indexB
                  })
                }

                component.products = products
                break
              } else {
                component.products = []
              }
              break
            case 'CustomProductGrid':
              const fetchTags = component.fetchProductTags
              let customGridQuery: string | null
              const customTagMode = component.combineTags ? 'all' : 'any'

              let isSoldOnline = false
              if (fetchTags?.includes('sold_online')) {
                fetchTags.splice(fetchTags.indexOf('sold_online'), 1)
                isSoldOnline = true
              }

              if (fetchTags && fetchTags.length > 0) {
                switch (customTagMode) {
                  case 'all':
                    customGridQuery = fetchTags
                      .map((tag) => `tag:"${escapeString(tag)}"`)
                      .join(' AND ')
                    break
                  case 'any':
                    customGridQuery = fetchTags
                      .map((tag) => `tag:"${escapeString(tag)}"`)
                      .join(' OR ')
                    break
                }
              }

              console.time('custom-product-grid')

              const excluded = ['type_fabric', 'cat_spareparts'].filter(
                (tag) => !fetchTags.includes(tag),
              )
              if (excluded.length > 0) {
                const notQueries = excluded
                  .map((tag) => `NOT tag:"${tag}"`)
                  .join(' AND ')
                customGridQuery = customGridQuery
                  ? `(${customGridQuery})  ${notQueries ? `AND (${notQueries})` : ''}`
                  : notQueries
              }

              let gridProducts = await getCustomProductTiles({
                channel,
                currency,
                query: customGridQuery,
                urlParams: queryParams,
                fetchAdditionalVariants:
                  fetchTags.indexOf('type_Dining-Chair') > -1,
              })

              console.timeEnd('custom-product-grid')

              if (isSoldOnline) {
                gridProducts = gridProducts.filter((product) =>
                  product.variants.edges?.some((variant) =>
                    isAvailableForPurchase(variant.node.availability),
                  ),
                )
              }

              component.componentPriority = component.products ?? []
              component.products = gridProducts ?? []
              component.showVariants = component.showVariants ?? true

              break

            case 'ProductGrid':
              console.time('product-grid')

              const filterTags: string[] =
                (component?.fetchProductTags
                  ? component?.fetchProductTags
                  : page?.tags) ?? []

              const productsFromTags: boolean =
                component.productsFromPageTags && filterTags.length > 0
              const tagMode = component.combineTags ? 'all' : 'any'
              let query: string | null = null

              const soldOnline = filterTags?.includes('sold_online')

              if (filterTags?.includes('sold_online')) {
                filterTags.splice(filterTags.indexOf('sold_online'), 1)
              }

              if (filterTags && filterTags.length > 0) {
                switch (tagMode) {
                  case 'all':
                    query = filterTags
                      .map((tag) => `tag:"${escapeString(tag)}"`)
                      .join(' AND ')
                    break
                  case 'any':
                    query = filterTags
                      .map((tag) => `tag:"${escapeString(tag)}"`)
                      .join(' OR ')
                    break
                }
              }
              const variantIds = component.variantsV2?.map(String)
              const productIds = component.productsV2?.map(String)

              if (productIds && !productsFromTags) {
                query = component.productsV2
                  .map((productId) => `id:"${productId}"`)
                  .join(' OR ')
              }

              if (variantIds && !productsFromTags) {
                query = variantIds
                  .map((variantId) => `variant_id:"${variantId}"`)
                  .join(' OR ')
              }

              let products = await getProductTiles({
                channel,
                currency,
                query,
                includeAllVariants: variantIds && !productsFromTags,
              })

              // if !productsFromTags, we need to sort the products, based on the productV2 order
              if (productIds && !variantIds) {
                // move the products that are in the productV2 to the top, if not found in the productV2, keep the position

                products = products.sort((a, b) => {
                  const indexA = productIds.findIndex((x) => x == a.id)
                  const indexB = productIds.findIndex((x) => x == b.id)
                  if (indexA === -1) return 1
                  if (indexB === -1) return -1
                  return indexA - indexB
                })
              }

              if (variantIds) {
                products = variantIds
                  .map((variantId) => {
                    // find product that contains this variantId
                    const product = products.find((product) =>
                      //@ts-expect-error
                      product.variants.edges.some(
                        (variant) => variant.node.id === variantId,
                      ),
                    )

                    if (!product) return null

                    // clone this product
                    const clonedProduct = {
                      ...product,
                      // remove all other variants than the one that matches the variantId
                      variants: {
                        //@ts-expect-error
                        edges: product?.variants.edges
                          .filter((variant) => variant.node.id === variantId)
                          .map((variant) => ({ ...variant.node })),
                      },
                    }

                    return {
                      ...clonedProduct,
                      heroVariant: {
                        ...clonedProduct?.variants.edges[0],
                      },
                    }
                  })
                  .filter((product) => product !== null)
              }

              if (soldOnline) {
                products = products.filter((product) =>
                  isAvailableForPurchase(product.heroVariant?.availability),
                )
              }
              // set tags
              // component.tags = tagList ?? null
              console.timeEnd('product-grid')
              component.products = products

              break
          }
        } catch (ex) {
          console.log(ex)
        }

        return component
      },
    ),
  )
  return page
}

//create a function to secape "-" in the string
export const escapeString = (str: string) => {
  return str.replace(/-/g, '\\-')
}

export const traverseToFindImagesOnPage = (
  component: any,
  level = 0,
): MediaPicker[] => {
  let list: MediaPicker[] = []
  if (!component) return list
  let children = Object.keys(component)
  if (children.length <= 1) {
    return list
  }

  children.forEach((key) =>
    list.push(...traverseToFindImagesOnPage(component[key], level + 1)),
  )

  if (
    component?.__typename === 'MediaWithCrops' &&
    component.media?.__typename === 'Image'
  ) {
    list.push(component)
  }

  return list
}
